<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import {
  getInternationalBankAccountsAction,
  getInternationalPaypalBankAccountsAction,
  getInternationalRevolutBankAccountsAction,
  getInternationalWiseBankAccountsAction,
  getIranianBankAccountsAction,
  getIranianSrcBankAccountsAction,
} from '@/api/functions/bank-account/get-bank-accounts-action'
import {
  editTransactionAction,
  getUserUnusedDiscountsForTransactionAction,
} from '@/api/functions/transactions/get-transaction-details.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  transaction: {
    type: Object,
  },
  users: {
    type: Array,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const queryClient = useQueryClient()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toSelectOptions(arr: any[]) {
  return (
    arr?.map((item) => ({
      label: item.title || item.bankName || item.ownerFullName || item.accountNumber || '—',
      value: item.id,
    })) ?? []
  )
}

const { transferMethods, transactionsStatusOptions, countries } = useGeneralSettings()

// Validation schema
const schema = yup.object({
  buyer: yup.string().required('خریدار الزامی است'),
  seller: yup.string().required('فروشنده الزامی است'),
  amount: yup.number().required('حجم الزامی است').typeError('باید عدد باشد'),
  exchange_rate: yup.number().required('نرخ تبدیل الزامی است').typeError('باید عدد باشد'),
  status: yup.string().required('وضعیت الزامی است'),
  buyer_transfer_methods: yup.array().required('روش انتقال خریدار الزامی است'),
  seller_transfer_methods: yup.array().required('روش انتقال فروشنده الزامی است'),
  // buyer_source_bank_account: yup.string().required('حساب مبدا ریالی خریدار الزامی است'),
  buyer_bank_account: yup.string().nullable(),
  buyer_paypal_bank_account: yup.string().nullable(),
  buyer_revolut_bank_account: yup.string().nullable(),
  buyer_wise_bank_account: yup.string().nullable(),
  seller_bank_account: yup.string().required('حساب فروشنده الزامی است'),
  seller_bank_account_country: yup.string().required('کشور حساب ارزی فروشنده الزامی است'),
  buyer_discount: yup.string().nullable(),
  seller_discount: yup.string().nullable(),
})

// Form setup
const { handleSubmit, errors, resetForm, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    buyer: '',
    seller: '',
    amount: null,
    exchange_rate: null,
    status: '',
    buyer_transfer_methods: [],
    seller_transfer_methods: [],
    buyer_source_bank_account: '',
    buyer_bank_account: null,
    buyer_paypal_bank_account: null,
    buyer_revolut_bank_account: null,
    buyer_wise_bank_account: null,
    seller_bank_account: '',
    seller_bank_account_country: '',
    buyer_discount: 'null',
    seller_discount: 'null',
  },
})

// Fields
const { value: buyer } = useField('buyer')
const { value: seller } = useField('seller')
const { value: amount } = useField('amount')
const { value: exchange_rate } = useField('exchange_rate')
const { value: status } = useField('status')
const { value: buyer_transfer_methods } = useField<string[]>('buyer_transfer_methods')
const { value: seller_transfer_methods } = useField<string[]>('seller_transfer_methods')
const { value: buyer_source_bank_account } = useField('buyer_source_bank_account')
const { value: buyer_bank_account } = useField('buyer_bank_account')
const { value: buyer_paypal_bank_account } = useField('buyer_paypal_bank_account')
const { value: buyer_revolut_bank_account } = useField('buyer_revolut_bank_account')
const { value: buyer_wise_bank_account } = useField('buyer_wise_bank_account')
const { value: seller_bank_account } = useField('seller_bank_account')
const { value: seller_bank_account_country } = useField('seller_bank_account_country')
const { value: buyer_discount } = useField('buyer_discount')
const { value: seller_discount } = useField('seller_discount')

// Computed
const showBuyerBankAccount = computed(() => {
  const bankTransfer = transferMethods.value?.find((method) => method.title === 'Bank Transfer')
  return buyer_transfer_methods.value?.includes(bankTransfer?.value ?? '')
})

const showBuyerPaypalAccount = computed(() => {
  const paypal = transferMethods.value?.find((method) => method.title === 'Paypal')
  return buyer_transfer_methods.value?.includes(paypal?.value ?? '')
})

const showBuyerRevolutAccount = computed(() => {
  const revolutIds =
    transferMethods.value?.filter((method) => method.title?.startsWith('Revolut')).map((method) => method.value) ?? []

  return buyer_transfer_methods.value?.some((id) => revolutIds.includes(id))
})
const showBuyerWiseAccount = computed(() => {
  const wise = transferMethods.value?.find((method) => method.title === 'Wise')
  return buyer_transfer_methods.value?.includes(wise?.value ?? '')
})

const buyerUuid = computed(() => {
  return props.transaction?.buyer?.uuid || buyer.value || ''
})

const sellerUuid = computed(() => {
  return seller.value || props.transaction?.seller?.uuid || ''
})

const buyerCurrentDiscountId = computed(() => {
  return props.transaction?.discounts?.buyer?.id ?? null
})

const sellerCurrentDiscountId = computed(() => {
  return props.transaction?.discounts?.seller?.id ?? null
})

const { data: buyerDiscountsResponse } = useQueryApi({
  queryKey: ['transactionBuyerDiscounts', buyerUuid, buyerCurrentDiscountId],
  queryFn: () => getUserUnusedDiscountsForTransactionAction(buyerUuid.value, buyerCurrentDiscountId.value),
  enabled: computed(() => !!buyerUuid.value),
})

const { data: sellerDiscountsResponse } = useQueryApi({
  queryKey: ['transactionSellerDiscounts', sellerUuid, sellerCurrentDiscountId],
  queryFn: () => getUserUnusedDiscountsForTransactionAction(sellerUuid.value, sellerCurrentDiscountId.value),
  enabled: computed(() => !!sellerUuid.value),
})

const { data: sellerAccountsResponse } = useQueryApi({
  queryKey: ['sellerBankAccounts', sellerUuid],
  queryFn: () => getIranianBankAccountsAction(sellerUuid.value),
  enabled: computed(() => !!sellerUuid.value),
})

const { data: buyerIranianResponse } = useQueryApi({
  queryKey: ['buyerIranian', buyerUuid],
  queryFn: () => getIranianSrcBankAccountsAction(buyerUuid.value),
  enabled: computed(() => !!buyerUuid.value),
})

const { data: buyerRevolutResponse } = useQueryApi({
  queryKey: ['buyerRevolut', buyerUuid],
  queryFn: () => getInternationalRevolutBankAccountsAction(buyerUuid.value),
  enabled: computed(() => !!buyerUuid.value),
})

const { data: buyerPaypalResponse } = useQueryApi({
  queryKey: ['buyerPaypal', buyerUuid],
  queryFn: () => getInternationalPaypalBankAccountsAction(buyerUuid.value),
  enabled: computed(() => !!buyerUuid.value),
})

const { data: buyerWiseResponse } = useQueryApi({
  queryKey: ['buyerWise', buyerUuid],
  queryFn: () => getInternationalWiseBankAccountsAction(buyerUuid.value),
  enabled: computed(() => !!buyerUuid.value),
})

const { data: buyerInternationalResponse } = useQueryApi({
  queryKey: ['buyerInternational', buyerUuid],
  queryFn: () => getInternationalBankAccountsAction(buyerUuid.value),
  enabled: computed(() => !!buyerUuid.value),
})

// const sellerIranianSrc = computed(() => toSelectOptions(sellerAccountsResponse?.value?.data ?? []))

// const buyerIrIraniananianSrc = computed(() =>
//   toSelectOptions(buyerIranianResponse?.value?.data ?? []),
// )
const sellerIranianSrc = computed(() =>
  (sellerAccountsResponse?.value?.data ?? []).map((account) => ({
    label: `${account.bankName} - ${account.cardNumber || account.shebaNumber}`,
    value: account.id,
    disabled: !account.verified,
  }))
)

const buyerIrIraniananianSrc = computed(() =>
  (buyerIranianResponse?.value?.data ?? []).map((account) => ({
    label: `${account.bankName} - ${account.cardNumber || account.shebaNumber}`,
    value: account.id,
    disabled: !account.verified,
  }))
)
const buyerRevolut = computed(() => toSelectOptions(buyerRevolutResponse?.value?.data ?? []))
const buyerPaypal = computed(() => toSelectOptions(buyerPaypalResponse?.value?.data ?? []))
const buyerInternational = computed(() => toSelectOptions(buyerInternationalResponse?.value?.data ?? []))
const buyerWise = computed(() => toSelectOptions(buyerWiseResponse?.value?.data ?? []))

const buyerDiscounts = computed(() => buyerDiscountsResponse.value?.data || [])
const sellerDiscounts = computed(() => sellerDiscountsResponse.value?.data || [])

const buyerDiscountOptions = computed(() => {
  const list = Array.isArray(buyerDiscounts.value) ? [...buyerDiscounts.value] : []

  const currentDiscount = props.transaction?.discounts?.buyer

  if (currentDiscount?.id != null && !list.some((d) => String(d.id) === String(currentDiscount.id))) {
    list.unshift(currentDiscount)
  }

  return [
    { label: 'بدون تخفیف', value: 'null' },
    ...list
      .filter((d) => d.id != null)
      .map((d) => ({
        label: d.title,
        value: String(d.id),
      })),
  ]
})

const sellerDiscountOptions = computed(() => {
  const list = Array.isArray(sellerDiscounts.value) ? [...sellerDiscounts.value] : []

  const currentDiscount = props.transaction?.discounts?.seller

  if (currentDiscount?.id != null && !list.some((d) => String(d.id) === String(currentDiscount.id))) {
    list.unshift(currentDiscount)
  }

  return [
    { label: 'بدون تخفیف', value: 'null' },
    ...list
      .filter((d) => d.id != null)
      .map((d) => ({
        label: d.title,
        value: String(d.id),
      })),
  ]
})

// Watch for transaction changes
const setTransactionFormValues = (transaction: any) => {
  if (!transaction) return

  const isBuyReq = transaction.requisition?.type === 'BUY'

  const buyerSrc = isBuyReq ? transaction.requisition?.transferMethods : transaction.offer?.transferMethods

  const sellerSrc = isBuyReq ? transaction.offer?.transferMethods : transaction.requisition?.transferMethods

  const buyer_transfer_methods = (buyerSrc ?? transaction.transferMethods ?? []).map((tm: any) => tm.id)

  const seller_transfer_methods = (sellerSrc ?? transaction.transferMethods ?? []).map((tm: any) => tm.id)

  setValues({
    buyer: transaction.buyer?.uuid || '',
    seller: transaction.seller?.uuid || '',
    amount: transaction.currencyAmount || null,
    exchange_rate: transaction.exchangeRate || null,
    status: transaction.status || '',
    buyer_transfer_methods,
    seller_transfer_methods,
    buyer_source_bank_account: transaction.sourceBankAccounts?.buyer?.id || '',
    buyer_bank_account: transaction.bankAccounts?.buyerInternationalBank?.id || null,
    buyer_paypal_bank_account: transaction.bankAccounts?.buyerInternationalPaypalBank?.id || null,
    buyer_revolut_bank_account: transaction.bankAccounts?.buyerInternationalRevolutBank?.id || null,
    seller_bank_account: transaction.bankAccounts?.sellerBank?.id || '',
    buyer_wise_bank_account: transaction.bankAccounts?.buyerInternationalWiseBank?.id || null,
    seller_bank_account_country: transaction.bankAccountCountries?.seller?.id || '',
    buyer_discount: transaction.discounts?.buyer?.id ? String(transaction.discounts.buyer.id) : 'null',
    seller_discount: transaction.discounts?.seller?.id ? String(transaction.discounts.seller.id) : 'null',
  })
}

watch(
  () => props.transaction,
  (transaction) => {
    if (transaction && props.modelValue) {
      setTransactionFormValues(transaction)
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && props.transaction) {
      setTransactionFormValues(props.transaction)
    }
  }
)

// Mutation
const editTransactionMutation = useMutationApi({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: ({ id, payload }: { id: string; payload: any }) => editTransactionAction(id, { ...payload }),
  onSuccess: () => {
    toast.success('تراکنش با موفقیت ویرایش شد', 'موفقیت')
    emit('confirm')
    closeModal()
    queryClient.invalidateQueries({ queryKey: ['transactionDetails'] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Submit
const onSubmit = handleSubmit((values) => {
  if (!props.transaction?.uuid) {
    toast.error('تراکنشی برای ویرایش انتخاب نشده است', 'خطا')
    return
  }

  const payload = {
    ...values,
    buyer_discount: values.buyer_discount === 'null' ? null : Number(values.buyer_discount),
    seller_discount: values.seller_discount === 'null' ? null : Number(values.seller_discount),
  }

  editTransactionMutation.mutate({
    id: props.transaction.uuid,
    payload,
  })
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>
<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="750px"
    background="#FFF" :closable="true" title="ویرایش تراکنش" closeDirection="left" class="update-request">
    <form @submit="onSubmit" class="flex h-[650px] flex-col">
      <div class="flex-1 space-y-4 px-5 py-5 pb-6">
        <!-- Buyer -->
        <section>
          <div class="mb-4 flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-[#2d5396]" />
            <h3 class="text-sm font-semibold text-[#303b4f]">
              اطلاعات خریدار
            </h3>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <MultiSelect variant="checkbox" v-model="buyer_transfer_methods" :error="errors.buyer_transfer_methods"
              label="روش‌های انتقال" :options="transferMethods" />

            <Select v-if="showBuyerBankAccount" variant="checkbox" v-model="buyer_bank_account"
              :error="errors.buyer_bank_account" label="حساب مقصد ارزی خریدار" :options="buyerInternational" />

            <Select v-if="showBuyerPaypalAccount" variant="checkbox" v-model="buyer_paypal_bank_account"
              :error="errors.buyer_paypal_bank_account" label="حساب مقصد ارزی پی‌پال خریدار" :options="buyerPaypal" />

            <Select v-if="showBuyerRevolutAccount" variant="checkbox" v-model="buyer_revolut_bank_account"
              label="حساب مقصد ارزی رولوت خریدار" :error="errors.buyer_revolut_bank_account" :options="buyerRevolut" />
           <Select v-if="showBuyerWiseAccount" variant="checkbox" v-model="buyer_wise_bank_account"
              :error="errors.buyer_wise_bank_account" label="حساب مقصد ارزی وایز خریدار" :options="buyerWise" />

            <Select variant="checkbox" v-model="buyer_source_bank_account" :error="errors.buyer_source_bank_account"
              label="حساب مبدا ریالی خریدار" :options="buyerIrIraniananianSrc" />

            <Select variant="checkbox" v-model="buyer_discount" :error="errors.buyer_discount" label="تخفیف خریدار"
              :options="buyerDiscountOptions" />
          </div>
        </section>

        <!-- Divider -->
        <div class="border-t border-dashed border-gray-200" />

        <!-- Seller -->
        <section>
          <div class="mb-4 flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-[#2d5396]" />
            <h3 class="text-sm font-semibold text-[#303b4f]">
              اطلاعات فروشنده
            </h3>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <MultiSelect variant="checkbox" v-model="seller_transfer_methods" :error="errors.seller_transfer_methods"
              label="روش‌های انتقال" :options="transferMethods" />

            <Select variant="checkbox" v-model="seller_bank_account" :error="errors.seller_bank_account"
              label="حساب فروشنده" :options="sellerIranianSrc" />

            <Select variant="checkbox" v-model="seller_bank_account_country" :error="errors.seller_bank_account_country"
              label="کشور حساب ارزی مبدا فروشنده" :options="countries" />

            <Select variant="checkbox" v-model="seller_discount" :error="errors.seller_discount" label="تخفیف فروشنده"
              :options="sellerDiscountOptions" />
          </div>
        </section>

        <!-- Divider -->
        <div class="border-t border-dashed border-gray-200" />

        <!-- Transaction -->
        <section>
          <div class="mb-3 flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-[#2d5396]" />
            <h3 class="text-sm font-semibold text-[#303b4f]">
              اطلاعات تراکنش
            </h3>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <Input v-model="amount" name="amount" :error="errors.amount" label="حجم" type="number" />

            <Input v-model="exchange_rate" name="exchange_rate" :error="errors.exchange_rate" label="نرخ تبدیل به تومان"
              type="number" />

            <Select variant="checkbox" v-model="status" :error="errors.status" label="وضعیت"
              :options="transactionsStatusOptions" />
          </div>
        </section>
      </div>

      <!-- Actions -->
      <div
        class="sticky bottom-0 z-50 flex w-full shrink-0 justify-between gap-6 border-gray-200 bg-white px-2 pb-9 pt-3">
        <Button type="submit" label="تایید ویرایش تراکنش" :isAdmin="true" class="h-12 w-9/12 rounded-xl" />

        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="h-12 w-4/12 rounded-xl border-2" />
      </div>
    </form>
  </Modal>
</template>
